import { useQuery } from '@tanstack/react-query';

import { getClassPackGroups } from '@/services/class-pack-groups';
import { Class } from './class-groups';
import { EntityImage } from '@/lib/entity-image';

export interface ClassPack {
  class_pack_id: string;
  class_pack_group_id: string;
  admin_user_id: string;
  tenant_id: string;
  status: 'ACTIVE' | 'INACTIVE' | 'DELETED';
  order: number;
  name: string;
  short_description: string;
  description: string | null;
  image: string | null;
  images?: EntityImage[];
  slug: string;
  availability: 'PUBLIC' | 'PRIVATE' | 'UNAVAILABLE';
  price: number | null;
  currency: string;
  total_number_of_classes: number | null;
  expiry_days: number | null;
  one_time_purchase_only: boolean;
  new_customers_only: boolean;
  is_popular: boolean;
  // Guests per pack; null while guest passes are off or the pack isn't eligible.
  guest_passes?: number | null;
  guest_pass_new_guests_only?: boolean;
  // On the customer's own codes: its credits pay only for private classes.
  is_custom_class_pack?: boolean;
  created_at: string;
  updated_at: string;
  class_pack_classes?: {
    class_pack_class_id: string;
    class_pack_id: string;
    class_id: string;
    class?: Pick<Class, 'name' | 'slug'>;
  }[];
}

export interface ClassPackGroup {
  class_pack_group_id: string;
  admin_user_id: string;
  tenant_id: string;
  name: string;
  description: string;
  slug: string;
  // Not sent by the public list, which returns ACTIVE groups only.
  status?: 'ACTIVE' | 'INACTIVE' | 'DELETED';
  class_packs: ClassPack[];
}

// class_id narrows the list to the packs that cover that class.
export default function useClassPackGroups({
  include_class_packs,
  class_id,
  enabled,
}: {
  include_class_packs: boolean;
  class_id?: string;
  enabled?: boolean;
}) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['classPackGroups', include_class_packs, class_id ?? null],
    queryFn: () => getClassPackGroups({ include_class_packs, class_id }),
    enabled,
    // The home page tabs wait on this and the tab inside observes it, so the
    // same loop as useClassGroups.
    retryOnMount: false,
  });

  return {
    data,
    isLoading,
    error,
    refetch,
  };
}
